/**
 * AriaPiano — Tab "Piano e fatturazione".
 * Stato subscription · trial info · cancel/resume · Stripe Customer Portal · CTA upgrade.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ArrowRight, Crown, Loader, ExternalLink, AlertCircle, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAriaCustomer } from "./AriaCustomerArea";

const TIER_INFO = {
    free: { label: "Aria Free", price: "€0 per sempre", color: "#9EC3FF", c2: "#4F8BFF" },
    welcome: { label: "Welcome Layer", price: "€19/mese", color: "#86EFAC", c2: "#10B981" },
    full: { label: "Full Experience", price: "€49/mese", color: "#A855F7", c2: "#7C3AED" },
    pro: { label: "Full Experience", price: "€49/mese", color: "#A855F7", c2: "#7C3AED" },
    cinematic: { label: "Cinematic Experience", price: "€99/mese", color: "#FF1A5C", c2: "#A855F7" },
};

export default function AriaPiano() {
    const { sub, planTier } = useAriaCustomer();
    const [portalLoading, setPortalLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");

    const info = TIER_INFO[planTier] || TIER_INFO.free;

    const openPortal = async () => {
        setPortalLoading(true); setError("");
        try {
            const { data } = await api.post("/billing/stripe/portal", { return_url: `${window.location.origin}/app/aria/piano` });
            if (data.portal_url) window.location.href = data.portal_url;
        } catch (e) { setError(formatApiError(e)); }
        setPortalLoading(false);
    };

    const cancelSub = async () => {
        if (!window.confirm("Sicuro di voler cancellare il rinnovo automatico? Aria continuerà a funzionare fino alla fine del periodo pagato.")) return;
        setActionLoading("cancel"); setError(""); setMsg("");
        try {
            await api.post("/billing/cancel");
            setMsg("Rinnovo automatico cancellato. Aria resta attiva fino alla scadenza del periodo.");
        } catch (e) { setError(formatApiError(e)); }
        setActionLoading(null);
    };

    const resumeSub = async () => {
        setActionLoading("resume"); setError(""); setMsg("");
        try {
            await api.post("/billing/resume");
            setMsg("Rinnovo automatico riattivato.");
        } catch (e) { setError(formatApiError(e)); }
        setActionLoading(null);
    };

    const isLifetime = !!sub?.is_lifetime;
    const isTrialing = sub?.status === "trialing";
    const isActive = sub?.status === "active";
    const isFree = planTier === "free" && !sub?.status;
    const isCanceling = sub?.cancel_at_period_end;

    return (
        <div className="max-w-3xl" data-testid="tab-piano">
            <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Piano e fatturazione</h1>
            <p className="text-white/65 text-[14px] mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
                Gestisci il tuo piano Aria, vedi le ricevute e cambia metodo di pagamento.
            </p>

            {/* Card stato corrente */}
            <div className="rounded-2xl p-6 mb-6 relative overflow-hidden"
                style={{ background: "rgba(15,18,28,0.7)", border: `1px solid ${info.color}55`, backdropFilter: "blur(20px)" }}
                data-testid="current-plan-card">
                <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${info.color}, ${info.c2})` }}>
                        {isLifetime ? <Crown size={16} color="#1a0c00" /> : <Sparkles size={16} color="#fff" />}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-[20px]" style={{ fontWeight: 500, letterSpacing: "-0.01em" }}>{info.label}</span>
                            {isLifetime && <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "linear-gradient(135deg,#FFD66B,#FF6B3D)", color: "#1a0c00", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>LIFETIME · Founder #{String(sub.founder_number || 0).padStart(3, "0")}</span>}
                            {!isLifetime && isTrialing && <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(255,193,7,0.15)", color: "#FFC107", fontFamily: "JetBrains Mono, monospace" }}>15 GG GRATIS</span>}
                            {!isLifetime && isActive && <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(134,239,172,0.15)", color: "#86EFAC", fontFamily: "JetBrains Mono, monospace" }}>ATTIVO</span>}
                            {isCanceling && <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.2em]" style={{ background: "rgba(255,193,7,0.15)", color: "#FFC107", fontFamily: "JetBrains Mono, monospace" }}>IN DISDETTA</span>}
                        </div>
                        <div className="text-white/65 text-[13.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {isLifetime ? "Pagato una volta, attivo per sempre" : info.price}
                            {sub?.billing_cycle === "yearly" && " · fatturato annualmente"}
                        </div>
                        {sub?.trial_end && isTrialing && (
                            <div className="text-white/55 text-[12px] mt-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                Trial finisce il {new Date(sub.trial_end).toLocaleDateString("it-IT")}
                            </div>
                        )}
                        {sub?.current_period_end && !isLifetime && (
                            <div className="text-white/55 text-[12px] mt-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {isCanceling ? "Scade il " : "Prossimo rinnovo: "}{new Date(sub.current_period_end).toLocaleDateString("it-IT")}
                            </div>
                        )}
                    </div>
                </div>

                {msg && (
                    <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(134,239,172,0.1)", color: "#86EFAC", border: "1px solid rgba(134,239,172,0.3)" }} data-testid="success-msg">
                        {msg}
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-3 rounded-xl text-[12px] flex items-start gap-2" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>
                        <AlertCircle size={12} className="flex-none mt-0.5" /> {error}
                    </div>
                )}

                {/* Azioni */}
                <div className="flex flex-wrap gap-2.5">
                    {isFree && (
                        <Link to="/aria/piani" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] transition hover:scale-[1.02]"
                            style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }} data-testid="upgrade-cta">
                            Scopri i piani <ArrowRight size={12} />
                        </Link>
                    )}
                    {(isActive || isTrialing) && !isLifetime && (
                        <>
                            <Link to="/aria/piani" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] transition hover:scale-[1.02]"
                                style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }} data-testid="change-plan-cta">
                                Cambia piano <ArrowRight size={12} />
                            </Link>
                            <button type="button" onClick={openPortal} disabled={portalLoading}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] text-white text-[13px] disabled:opacity-60"
                                data-testid="open-portal">
                                {portalLoading ? <Loader size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                                Ricevute & metodo di pagamento
                            </button>
                            {!isCanceling && (
                                <button type="button" onClick={cancelSub} disabled={actionLoading === "cancel"}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-transparent hover:bg-white/[0.04] text-white/65 hover:text-white text-[13px] disabled:opacity-60"
                                    data-testid="cancel-sub">
                                    {actionLoading === "cancel" ? <Loader size={12} className="animate-spin" /> : null} Cancella rinnovo
                                </button>
                            )}
                            {isCanceling && (
                                <button type="button" onClick={resumeSub} disabled={actionLoading === "resume"}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] text-white text-[13px] disabled:opacity-60"
                                    data-testid="resume-sub">
                                    {actionLoading === "resume" ? <Loader size={12} className="animate-spin" /> : null} Riattiva rinnovo
                                </button>
                            )}
                        </>
                    )}
                    {isLifetime && (
                        <div className="text-white/55 text-[12.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            🏛️ Nessuna fattura: hai pagato una volta. Riceverai newsletter Investor Updates mensile.
                        </div>
                    )}
                </div>
            </div>

            {/* Quick info benefits */}
            <div className="rounded-2xl p-5" style={{ background: "rgba(15,18,28,0.4)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-2 mb-3">
                    <CreditCard size={12} className="text-white/45" />
                    <span className="text-[10.5px] uppercase tracking-[0.28em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>Come funziona la fatturazione</span>
                </div>
                <ul className="space-y-1.5 text-white/65 text-[12.5px] list-disc pl-5" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <li>Pagamento sicuro via Stripe o PayPal</li>
                    <li>Rinnovo automatico mensile o annuale (puoi disdire in ogni momento)</li>
                    <li>Le ricevute arrivano via email + sono scaricabili dal portal</li>
                    <li>15 giorni di prova gratuiti sui piani a pagamento — paghi solo se ti piace</li>
                    <li>Niente è permanente: in qualsiasi momento puoi tornare al piano Free senza perdere dati</li>
                </ul>
            </div>
        </div>
    );
}
